/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {dlsAlt, dlsColor} from '../theme/theme';

/**
 * Returns the status badge palette shared by every colored status cell.
 * Each entry pairs a 10% tint background with the saturated foreground
 * color used for the label, so badges read consistently across grids.
 *
 * ```typescript
 *
 *    import {getStatusColors} from '@ion/dls/src/hooks/useStatusBadgeColors';
 *
 *    ...
 *    const {valid, invalid} = getStatusColors();
 *    // valid.color is the tint, valid.textColor the label color...
 *
 * ```
 *
 * @returns The `valid`, `invalid`, `pending` and `other` color pairs.
 */
export const getStatusColors = () => ({
  valid : {
    textColor : dlsColor('caGreen'),
    color     : 'rgba(105, 178, 70, 0.1)'
  },
  invalid : {
    textColor : dlsColor('caRed'),
    color     : 'rgba(226, 35, 26, 0.1)'
  },
  pending : {
    textColor : dlsAlt('maps', 'colors', 'lightOrange'),
    color     : 'rgba(255, 150, 90, 0.1)'
  },
  other : {
    textColor : dlsColor('grey2'),
    color     : 'rgba(79, 79, 79, 0.1)'
  }
});

export interface StatusColorSets {
  positiveStatuses?: string[];
  negativeStatuses?: string[];
  pendingStatuses?: string[];
}

export interface UseStatusBadgeColorsProps extends StatusColorSets {
  status: string;
  display?: string;
  isPadding?: boolean;
}

/**
 * The badge color pair a status resolves to, as a plain function.
 *
 * This is the precedence itself, separated from the hook so a caller which
 * resolves a badge outside a render can apply it too - a notification adapter,
 * say, which fixes the badge when the notification is built rather than when a
 * row paints. The hook is a thin wrapper over this and the two can therefore
 * never disagree.
 *
 * Every set is optional and every test is guarded, so an omitted set means
 * "nothing matches it" rather than a thrown error.
 *
 * ```typescript
 *
 *    import {resolveStatusColors} from '@ion/dls/src/hooks/useStatusBadgeColors';
 *
 *    ...
 *    const {color, textColor} = resolveStatusColors('Cancelled', {
 *      positiveStatuses : [Status.COMPLETED],
 *      negativeStatuses : [Status.FAILED],
 *      pendingStatuses  : [Status.CANCELLED]
 *    });
 *    // color is the tint, textColor the label color...
 *
 * ```
 *
 * @param   status The status to resolve.
 * @param   sets The positive, negative and pending statuses to test against.
 * @returns The `valid`, `invalid`, `pending` or `other` pair, in that
 *          precedence, falling back to `other` when nothing matches.
 */
export const resolveStatusColors = (status?: string, {
  positiveStatuses,
  negativeStatuses,
  pendingStatuses
}: StatusColorSets = {}) => {
  const {invalid, other, pending, valid} = getStatusColors();
  let colors = other;

  if (positiveStatuses?.includes(status)) {
    colors = valid;
  } else if (negativeStatuses?.includes(status)) {
    colors = invalid;
  } else if (pendingStatuses?.includes(status)) {
    colors = pending;
  }

  return colors;
};

/**
 * Re-usable hook for determining badge colors.
 *
 * Given a status custom positive or negative statuses, will return either:
 *    1) Green: when the given status correlates to a positive status
 *    2) Red: when the given status correlates to a negative status
 *    3) Orange: when the given status correlates to a pending status
 *    4) Gray: for all others
 *
 * ```typescript
 *
 *    import {useStatusBadgeColors} from '@ion/dls/src/hooks/';
 *
 *    const {color, textColor} = useStatusBadgeColors({
 *      status, // 'COMPLETED', 'CANCELLED', etc...
 *      positiveStatuses : [Status.COMPLETED],
 *      negativeStatuses : [Status.FAILED, Status.CANCELLED]
 *    });
 *
 * ```
 */
export const useStatusBadgeColors = ({
  status,
  positiveStatuses,
  negativeStatuses,
  pendingStatuses
}: UseStatusBadgeColorsProps) => (
  resolveStatusColors(status, {positiveStatuses, negativeStatuses, pendingStatuses})
);

export default useStatusBadgeColors;
